import { useMemo, useState } from 'react'
import { Platform, Pressable, ScrollView, Text, View } from 'react-native'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useLocalSearchParams, useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { sessionQueryOptions } from '@/src/queries/library-queries'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Icon, symbolName } from '../../../components/ui/Icon'
import { ScreenLoadingFallback } from '../../../components/ui/ScreenLoadingFallback'
import { Spinner } from '../../../components/ui/Spinner'
import { colors } from '../../../theme'
import { typography } from '../../../theme/typography'
import { MarkdownText } from '../components/MarkdownText'
import { MediaImage } from '../components/MediaImage'
import { VisualMediaPlayer } from '../components/VisualMediaPlayer'
import { normalizeMediaUrl } from '../utils/media'
import { resolveMediaAspectRatio } from '../utils/media-aspect-ratio'
import { prefetchSessionMedia } from '../utils/prefetch-session-media'

const PREVIEW_CLOSE_FALLBACK_ROUTE = '/(protected)/(account)/(tabs)' as const
const PREVIEW_CTA_COLOR = '#291180'
const PREVIEW_DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9
const PREVIEW_DEFAULT_MEDIA_ASPECT_RATIO = 9 / 16

function normalizeParam(value: string | string[] | undefined) {
  if (Array.isArray(value)) return value[0]
  return value
}

function hasTextContent(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

export default function PreviewScreen() {
  const {
    id: idParam,
    weekId: weekIdParam,
    subjectId: subjectIdParam,
    programId: programIdParam,
    exerciseType: exerciseTypeParam,
  } = useLocalSearchParams<{
    id?: string | string[]
    weekId?: string | string[]
    subjectId?: string | string[]
    programId?: string | string[]
    exerciseType?: string | string[]
  }>()
  const id = normalizeParam(idParam)
  const weekId = normalizeParam(weekIdParam)
  const subjectId = normalizeParam(subjectIdParam)
  const programId = normalizeParam(programIdParam)
  const exerciseType = normalizeParam(exerciseTypeParam)
  const { back, canGoBack, replace } = useRouter()
  const insets = useSafeAreaInsets()
  const { t } = useTranslation(['session', 'common'])
  const language = useContentfulLocale()
  const {
    data: session,
    isLoading,
    error,
  } = useQuery({
    // queryFn only runs when enabled, which guarantees id is defined
    ...sessionQueryOptions(id as string, language),
    enabled: id != null,
  })
  const sessionMedia = session?.media ?? null
  const descriptionText = session?.description
  const previewDescription = hasTextContent(descriptionText)
    ? descriptionText
    : undefined
  const rawUrl = sessionMedia?.url ?? undefined
  const mediaUrl = normalizeMediaUrl(rawUrl)
  const contentType = sessionMedia?.contentType ?? ''
  const isVideo = contentType.startsWith('video/')
  const isImage = contentType.startsWith('image/')
  const visualFallbackAspectRatio = isVideo
    ? Platform.OS === 'web'
      ? PREVIEW_DEFAULT_MEDIA_ASPECT_RATIO
      : PREVIEW_DEFAULT_VIDEO_ASPECT_RATIO
    : PREVIEW_DEFAULT_MEDIA_ASPECT_RATIO
  const initialVisualAspectRatio = useMemo(() => {
    const details = sessionMedia?.details
    const initialWidth = details?.image?.width ?? details?.video?.width
    const initialHeight = details?.image?.height ?? details?.video?.height

    if (initialWidth == null || initialHeight == null) {
      return visualFallbackAspectRatio
    }

    return resolveMediaAspectRatio({
      width: initialWidth,
      height: initialHeight,
      isVideo,
      isWeb: Platform.OS === 'web',
      fallbackAspectRatio: visualFallbackAspectRatio,
    })
  }, [isVideo, sessionMedia?.details, visualFallbackAspectRatio])

  const [isPrefetchingMedia, setIsPrefetchingMedia] = useState(false)
  const footerBottomPadding = Math.max(insets.bottom + 16, 32)
  const scrollContentBottomPadding = 180 + insets.bottom

  const handleClose = () => {
    if (canGoBack()) {
      back()
      return
    }

    replace(PREVIEW_CLOSE_FALLBACK_ROUTE)
  }

  const handleStart = async () => {
    if (session == null || typeof id !== 'string' || isPrefetchingMedia) {
      return
    }

    setIsPrefetchingMedia(true)

    await prefetchSessionMedia(session).catch(() => undefined)
    setIsPrefetchingMedia(false)

    const nextParams: {
      id: string
      source: 'preview'
      weekId?: string
      subjectId?: string
      programId?: string
      exerciseType?: string
    } = {
      id,
      source: 'preview',
    }

    if (weekId != null) {
      nextParams.weekId = weekId
    }
    if (subjectId != null) {
      nextParams.subjectId = subjectId
    }
    if (programId != null) {
      nextParams.programId = programId
    }
    if (exerciseType != null) {
      nextParams.exerciseType = exerciseType
    }

    replace({
      pathname: '/session/[id]',
      params: nextParams,
    })
  }

  if (isLoading) {
    return (
      <ScreenLoadingFallback>
        <Text className="mt-3 text-base text-ink-secondary">
          {t('common:loading')}
        </Text>
      </ScreenLoadingFallback>
    )
  }

  if (error != null || session == null) {
    return (
      <View className="flex-1 justify-center items-center bg-background-primary">
        <Text className="text-base text-destructive mb-2.5">
          {t('session:preview_error')}
        </Text>
        <Pressable onPress={handleClose}>
          <Text className="text-base text-primary">{t('common:back')}</Text>
        </Pressable>
      </View>
    )
  }

  return (
    <View className="flex-1 bg-background-primary">
      <Head>
        <title>{t('common:page_title_preview_session')}</title>
      </Head>
      <SafeAreaView className="bg-background-primary pt-2.5">
        <View className="flex-row items-center px-5 pb-2.5">
          <Text className="flex-1 text-left" style={{ ...typography.label1 }}>
            {session.title}
          </Text>
          <Pressable
            onPress={handleClose}
            className="w-10 h-10 -mr-2 justify-center items-center"
          >
            <Icon name={symbolName('xmark', 'close')} size={28} color="black" />
          </Pressable>
        </View>
      </SafeAreaView>

      <ScrollView
        contentInset={{ bottom: scrollContentBottomPadding }}
        scrollIndicatorInsets={{ bottom: scrollContentBottomPadding }}
      >
        {mediaUrl != null && isVideo && (
          <VisualMediaPlayer
            key={`preview-video:${mediaUrl}`}
            source={{ uri: mediaUrl }}
            contentType={contentType}
            aspectRatio={initialVisualAspectRatio}
            fallbackAspectRatio={visualFallbackAspectRatio}
          />
        )}

        {mediaUrl != null && isImage && (
          <MediaImage
            key={`preview-image:${mediaUrl}`}
            source={{ uri: mediaUrl }}
            contentType={contentType}
            aspectRatio={initialVisualAspectRatio}
            fallbackAspectRatio={visualFallbackAspectRatio}
          />
        )}

        {previewDescription != null && (
          <MarkdownText
            content={previewDescription}
            style={{ paddingHorizontal: 20, marginBottom: 20 }}
          />
        )}
        <View style={{ height: scrollContentBottomPadding }} />
      </ScrollView>

      <View
        className="absolute bottom-0 left-0 right-0 p-5 bg-white"
        style={{ paddingBottom: footerBottomPadding }}
      >
        <Pressable
          className="w-full py-4 items-center justify-center"
          style={{
            backgroundColor: PREVIEW_CTA_COLOR,
            borderRadius: 8,
            opacity: isPrefetchingMedia ? 0.8 : 1,
          }}
          onPress={handleStart}
          disabled={isPrefetchingMedia}
        >
          {isPrefetchingMedia ? (
            <View className="flex-row items-center gap-2">
              <Spinner size="small" color={colors.primaryForeground} />
              <Text className="text-base font-semibold text-primary-foreground">
                {t('common:loading')}
              </Text>
            </View>
          ) : (
            <Text className="text-base font-semibold text-primary-foreground">
              {t('session:continue')}
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  )
}
